/* Oh Orchid — add-to-cart bridge + cart drawer discount row
   Added 2026-08-26. See claude/Oh_Orchid_Stage0_Checkout_Diagnosis_2026-08-26.md
                     and claude/Oh_Orchid_Stage0_Cart_Fix_EXECUTION_LOG_2026-08-26.md

   PART 1 — WHY THIS FILE EXISTS
   -----------------------------
   layout/theme.liquid renders Dawn's <cart-drawer>, but the section that loaded
   assets/cart-drawer.js — Dawn's sections/header.liquid — was replaced by
   sections/oo-header.liquid in the Claude Design conversion. The custom header
   carried across the drawer's four stylesheets and not its script, so
   <cart-drawer> rendered as an element whose class was never registered.

   assets/product-form.js (stock Dawn) resolves this.cart to that element and
   calls this.cart.getSectionsToRender() on submit. With no class, that threw
   `TypeError: this.cart.getSectionsToRender is not a function` BEFORE the
   /cart/add.js request was made. Nothing was ever added to the cart and the
   button was left spinning with no error shown. Every Add to cart on the site —
   product pages and collection quick-add — failed this way, and there is no
   dynamic checkout button, so there was no second path to purchase.

   Loading Dawn's cart-drawer.js would not fix it. Its constructor calls
   document.querySelector('#cart-icon-bubble').setAttribute(...) and this header
   has no #cart-icon-bubble — the bag is [data-oo-open-cart] — so the upgrade
   would throw in the constructor instead. It would also leave two competing
   drawers: Dawn's on add-to-cart, the header's own on the bag icon.

   So <cart-drawer> is registered below as a thin adapter over the header's own
   drawer, implementing only the members product-form.js calls and delegating to
   markup and helpers that already exist in sections/oo-header.liquid and
   assets/component-oo-nav.js.

   PART 2 — THE DISCOUNT ROW
   -------------------------
   With a cart-level discount code, Shopify leaves final_line_price equal to
   original_line_price and records the reduction in cart.cart_level_discount_-
   applications instead. The drawer renders lines from final_line_price and the
   subtotal from cart.total_price, and had no discount row — so a discounted cart
   showed, with nothing to explain the gap:

       Line item  $18        Subtotal  $13

   Dawn's drawer renders a discount list for exactly this reason. This adds the
   equivalent to the header's drawer.

   Why JavaScript rather than Liquid in oo-header.liquid: that file is 46KB of
   navigation markup and every collection link in the store, and the theme API
   replaces whole files rather than patching them. Rewriting it to add one row
   risks the site's entire navigation for a footer line. Nothing about a product
   is constructed here — the title and the amount both come from the cart JSON.

   PART 3 — EXACT AMOUNTS IN THE CART ONLY
   ---------------------------------------
   The shop's money format is "${{amount_no_decimals}}", so the storefront renders
   whole dollars — $78, not $78.00. That is the intended brand treatment and it
   stays.

   The cart is the exception. It is the one place the figures have to reconcile:
   a percentage discount produces cent-level amounts, and rounding each of them
   independently made the arithmetic visibly wrong — $18 − $5 = $13 against a
   real total of $12.60, and worse on other carts ($25 − $8 = $17 against $17.50).

   Every money figure in the drawer is therefore re-rendered here from the cart
   JSON with exact cents, after Liquid and after component-oo-nav.js have each
   had their turn. No Liquid money filter can do this: `money` and
   `money_without_trailing_zeros` both read shop.money_format, so they return
   whole dollars no matter which is used. Changing shop.money_format would fix
   the cart and break every price on the site.

   The free-shipping band is included, for a second reason. It was rounding the
   gap to the nearest dollar, so a cart $7.40 short read "$7 away" — a shopper who
   added exactly $7 would miss the threshold and be charged shipping at checkout,
   having been told they would not be. Exact cents removes the rounding, and
   matches how the figure appears at checkout.
*/
(function () {
  'use strict';

  /* ---------------------------------------------------------------- helpers */

  /* The section's schema sets "class": "oo-header-section", so Shopify wraps it
     in <div id="shopify-section-{{ section.id }}" class="oo-header-section">.
     Read the id from there rather than hard-coding it — it changes with the
     theme id, and a stale literal would fail silently. */
  function headerSectionId() {
    var el = document.querySelector('.oo-header-section[id^="shopify-section-"]');
    return el ? el.id.replace('shopify-section-', '') : null;
  }

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  /* Exact amount, always two decimals — $12.60, $1,234.05, −$5.40.
     Deliberately not Shopify.formatMoney: that follows shop.money_format, which
     is the no-decimals format this function exists to override inside the cart. */
  function money(cents) {
    var rounded = Math.round(Math.abs(cents));
    var dollars = Math.floor(rounded / 100).toLocaleString('en-US');
    var remainder = ('0' + (rounded % 100)).slice(-2);
    return (cents < 0 ? '-' : '') + '$' + dollars + '.' + remainder;
  }

  /* Shopify returns cart-level discounts in two different shapes, and the drawer
     is fed by both. /cart.js gives the application directly:

         { title: 'LOVELY30', total_allocated_amount: 1080 }

     while /cart/change.js — which component-oo-nav.js's +/- controls use — wraps
     it and moves the figure out one level:

         { amount: 1080, discount_application: { title: 'LOVELY30', ... } }

     Reading the first shape only produced "Discount −$0" after every quantity
     change. Normalise before rendering. */
  function normalizeApplication(application) {
    var detail = application.discount_application || application;
    var amount = application.amount != null
      ? application.amount
      : detail.total_allocated_amount;
    return { title: detail.title || 'Discount', amount: amount || 0 };
  }

  /* ------------------------------------------------------------ discount row */

  var ROW_ATTR = 'data-oo-cart-discounts';

  function discountContainer() {
    var existing = document.querySelector('[' + ROW_ATTR + ']');
    if (existing) return existing;

    /* Sits directly above the subtotal, inside the drawer footer, so the
       reduction reads immediately before the figure it produced. */
    var subtotal = document.querySelector('.oo-cart-foot .oo-subtotal');
    if (!subtotal || !subtotal.parentNode) return null;

    var el = document.createElement('div');
    el.setAttribute(ROW_ATTR, '');
    el.style.cssText = 'display:flex;flex-direction:column;gap:6px;margin-bottom:12px;';
    subtotal.parentNode.insertBefore(el, subtotal);
    return el;
  }

  function renderDiscounts(cart) {
    var el = discountContainer();
    if (!el) return;

    var applications = (cart && cart.cart_level_discount_applications) || [];

    /* Line-level discounts are already reflected in each line's price, so
       showing them here would double-count the reduction. Only cart-level
       allocations produce the unexplained gap this row exists to close. */
    if (!applications.length) {
      el.textContent = '';
      el.hidden = true;
      return;
    }

    el.hidden = false;
    el.textContent = '';

    applications.forEach(function (application) {
      var detail = normalizeApplication(application);

      var row = document.createElement('div');
      row.style.cssText =
        'display:flex;align-items:baseline;justify-content:space-between;gap:12px;' +
        'font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:#A08C5A;';

      var title = document.createElement('span');
      title.textContent = detail.title;

      var amount = document.createElement('span');
      amount.textContent = '−' + money(detail.amount);

      row.appendChild(title);
      row.appendChild(amount);
      el.appendChild(row);
    });
  }

  /* --------------------------------------------------------- item list sync */

  /* component-oo-nav.js's +/- handler finds the line to update by matching the
     cart line `key` it captured when Liquid rendered the drawer:

         var item = (cart.items || []).filter(i => i.key === key)[0];
         if (!item) { line.remove(); }

     Shopify rotates that key's hash whenever a line's discount allocation
     changes — and changing the quantity of a discounted cart changes the
     allocation. So on any cart carrying a discount code the lookup always
     misses, the handler takes the `line.remove()` branch, and because
     item_count is still non-zero the empty-state branch does not fire either.
     The customer is left looking at a completely blank drawer with a live
     subtotal and an active Checkout button.

     Observed: 1 x $18 with LOVELY30, key ...8dad0552; press +, key becomes
     ...8dbb4870, line removed, drawer blank, cart still holds 2 items.

     This is pre-existing and was unreachable while Add to cart was broken —
     nothing could get into the cart to press + on. Restoring add-to-cart makes
     it reachable on most orders, since four of the last six web orders used a
     discount code.

     Rather than patch the key matching inside that file, the item list is
     re-rendered from the server whenever it diverges from the cart. Liquid stays
     the only thing that renders a line, and the drawer self-corrects for any
     cause of divergence, not just this one. */
  function syncDrawerItems(cart) {
    var wrap = document.querySelector('[data-oo-cart-items]');
    if (!wrap || !cart || !cart.items) return;
    var rows = Array.prototype.slice.call(wrap.querySelectorAll('.oo-line'));
    if (rows.length === cart.items.length && rows.every(function (row, index) {
      var control = row.querySelector('[data-oo-key]');
      var quantity = row.querySelector('.oo-qty span');
      return control && quantity &&
        control.getAttribute('data-oo-key') === cart.items[index].key &&
        Number(quantity.textContent) === cart.items[index].quantity;
    })) return;

    var id = headerSectionId();
    if (!id) return;

    /* No Accept header. The Section Rendering API returns JSON on its own, and
       sending `Accept: application/json` to a /products/<handle> URL makes
       Shopify serve that product's JSON instead — the response comes back as
       {"product": {...}} with no section in it, and the drawer never heals.
       Verified: same URL without the header returns the section correctly. */
    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(id))
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections && sections[id];
        if (!html) return;
        var incoming = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-oo-cart-items]');
        if (!incoming) return;
        wrap.innerHTML = incoming.innerHTML;
        /* The re-render came from Liquid, so its prices are whole dollars again. */
        formatDrawerMoney(cart);
      })
      .catch(function () { /* leave the drawer as rendered */ });
  }

  /* ------------------------------------------------------- exact cart amounts */

  /* Liquid renders the line prices and the subtotal through the shop's
     no-decimals money format, and component-oo-nav.js re-renders the subtotal
     the same way after each AJAX change. Both are overwritten here with exact
     figures so the drawer reconciles. Lines are matched by index: the drawer and
     the cart JSON are rendered from the same cart in the same order, and the
     length guard skips the moment between a re-render starting and finishing. */
  function formatDrawerMoney(cart) {
    if (!cart || !cart.items) return;

    var lines = document.querySelectorAll('[data-oo-cart-items] .oo-line');
    if (lines.length === cart.items.length) {
      cart.items.forEach(function (item, index) {
        var price = lines[index].querySelector('.oo-line-foot > span');
        if (price) price.textContent = money(item.final_line_price);
      });
    }

    var subtotal = document.querySelector('[data-oo-cart-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.total_price);

    formatShippingBand(cart);
  }

  /* The free-shipping band, re-stated exactly.

     component-oo-nav.js rounded the remaining gap to the nearest dollar, so a
     cart $7.40 short of the threshold read "$7 away from complimentary
     shipping". A shopper who took that literally and added $7 would land at
     $74.60, miss the threshold, and be charged shipping at checkout after being
     told they would not be — the kind of small contradiction that loses an order
     at the last step.

     Exact cents removes the rounding entirely and matches the figures at
     checkout. The threshold is read from the drawer, which Liquid writes from
     the section setting, so it stays a single source of truth.

     The wording is copied from oo-header.liquid and component-oo-nav.js so the
     band reads identically whether Liquid or this file rendered it last. */
  function formatShippingBand(cart) {
    var band = document.querySelector('[data-oo-ship]');
    if (!band) return;

    var threshold = parseInt(band.getAttribute('data-oo-ship-threshold'), 10);
    if (!threshold || threshold <= 0) return;

    var total = cart.total_price || 0;
    var gap = threshold - total;

    var copy = band.querySelector('[data-oo-ship-copy]');
    if (copy) {
      copy.textContent = gap > 0
        ? money(gap) + ' away from complimentary shipping'
        : 'Complimentary shipping unlocked';
    }

    var bar = band.querySelector('[data-oo-ship-bar]');
    if (bar) bar.style.width = Math.min(100, Math.round((total / threshold) * 100)) + '%';
  }

  /* One entry point, so every path through the drawer leaves the same state. */
  function applyCart(cart) {
    renderDiscounts(cart);
    formatDrawerMoney(cart);
    return syncDrawerItems(cart);
  }

  function refreshFromServer() {
    var root = document.querySelector('[data-oo-root]');
    var wrap = root && root.querySelector('[data-oo-cart-items]');
    var checkout = root && root.querySelector('.oo-checkout');
    var wasDisabled = checkout ? checkout.disabled : true;
    var itemCount = null;
    if (wrap) {
      wrap.setAttribute('aria-busy', 'true');
      wrap.querySelectorAll('[data-oo-qty]').forEach(function (button) { button.disabled = true; });
    }
    if (checkout) checkout.disabled = true;
    fetch(cartUrl() + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        itemCount = cart.item_count || 0;
        return window.ooUpdateCart ? window.ooUpdateCart(cart) : applyCart(cart);
      })
      .catch(function () { /* leave the drawer as rendered */ })
      .then(function () {
        if (wrap) {
          wrap.removeAttribute('aria-busy');
          wrap.querySelectorAll('[data-oo-qty]').forEach(function (button) { button.disabled = false; });
        }
        if (checkout) checkout.disabled = itemCount === null ? wasDisabled : itemCount === 0;
      });
  }

  /* component-oo-nav.js recomputes the count, subtotal and free-shipping band
     from one cart payload after every AJAX change, including the drawer's +/-
     controls. The discount row has to move with them.

     It is wrapped through a property accessor rather than by reassignment
     because load order is not guaranteed: this file is loaded from
     layout/theme.liquid, which renders before {% sections 'header-group' %},
     so component-oo-nav.js usually assigns window.ooUpdateCart *after* this
     runs and a plain wrapper would be overwritten. The accessor captures the
     assignment whenever it happens. */
  var inner = window.ooUpdateCart;

  function wrapped(cart) {
    if (typeof inner === 'function') inner(cart);
    return applyCart(cart);
  }

  try {
    Object.defineProperty(window, 'ooUpdateCart', {
      configurable: true,
      get: function () { return wrapped; },
      set: function (fn) { inner = fn; }
    });
  } catch (e) {
    /* Some environment already made it non-configurable — fall back to polling
       the server after cart changes rather than losing the totals updater. */
    window.ooUpdateCart = wrapped;
  }

  /* --------------------------------------------------------- <cart-drawer> */

  if (!customElements.get('cart-drawer')) {
    customElements.define(
      'cart-drawer',
      class OoCartDrawerBridge extends HTMLElement {
        /* product-form.js calls this before it knows whether the add will
           succeed, so it must never throw. Pages without the header (checkout,
           password) get an empty list and still add to the cart correctly. */
        getSectionsToRender() {
          var id = headerSectionId();
          return id ? [{ id: id }] : [];
        }

        setActiveElement(element) {
          this.activeElement = element;
        }

        renderContents(response) {
          var id = headerSectionId();
          var html = response && response.sections && id ? response.sections[id] : null;

          /* Line items are rendered by Liquid in oo-header.liquid, so the newly
             added line comes back already formatted by the Section Rendering API
             rather than being rebuilt here. Nothing about the product is
             constructed in JavaScript. */
          if (html) {
            var incoming = new DOMParser()
              .parseFromString(html, 'text/html')
              .querySelector('[data-oo-cart-items]');
            var current = document.querySelector('[data-oo-cart-items]');
            if (incoming && current) current.innerHTML = incoming.innerHTML;
          }

          /* The add response describes the line that was added, not the cart, so
             the totals come from /cart.js. ooUpdateCart derives the count, the
             subtotal, the free-shipping band and now the discount row from that
             single payload — the same reason they are derived together in
             Liquid. */
          var self = this;
          fetch(cartUrl() + '.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              if (window.ooUpdateCart) window.ooUpdateCart(cart);
              var checkout = document.querySelector('.oo-checkout');
              if (checkout) checkout.disabled = (cart.item_count || 0) === 0;
            })
            .catch(function () { /* totals stay as rendered; the item is still in the cart */ })
            .then(function () { self.open(); });
        }

        /* Reuse the header's own open and close paths by clicking its controls.
           openPanel() and closeAll() live inside component-oo-nav.js's closure
           and are not exported, and they also trap focus and lock body scroll —
           setting the dataset directly would skip both. */
        open() {
          var btn = document.querySelector('[data-oo-open-cart]');
          if (btn) btn.click();
        }

        close() {
          var btn = document.querySelector('[data-oo-close-all]');
          if (btn) btn.click();
        }
      }
    );
  }

  /* Liquid renders the drawer on page load without the discount row and with
     whole-dollar amounts, so the first pass has to happen here too — otherwise
     the cart only becomes exact after the shopper touches something. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshFromServer);
  } else {
    refreshFromServer();
  }
})();
